import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  User, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { Member, Admin } from '../types';
import { MemberService } from '../services/firebaseService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

interface AuthContextType {
  user: User | null;
  member: Member | null;
  admin: Admin | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithMagicLink: (member: Member) => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: boolean;
  isMember: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [member, setMember] = useState<Member | null>(null);
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      
      if (user) {
        try {
          // Vérifier si c'est un membre
          const memberData = await MemberService.getMemberById(user.uid);
          if (memberData) {
            setMember(memberData);
            setAdmin(null);
          } else {
            // Vérifier si c'est un admin
            const adminDoc = await getDoc(doc(db, 'admins', user.uid));
            if (adminDoc.exists()) {
              const adminData = adminDoc.data() as Admin;
              setAdmin(adminData);
              setMember(null);
            } else {
              setMember(null);
              setAdmin(null);
            }
          }
        } catch (error) {
          console.error('Erreur lors de la récupération des données utilisateur:', error);
          setMember(null);
          setAdmin(null);
        }
      } else {
        setMember(null);
        setAdmin(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Erreur de connexion:', error);
      throw error;
    }
  };

  const loginWithMagicLink = async (member: Member) => {
    try {
      // Créer un utilisateur Firebase Auth temporaire pour le membre
      // Nous utilisons l'ID du membre comme UID et un mot de passe temporaire
      const tempPassword = `temp_${member.id}_${Date.now()}`;
      
      // Créer le compte Firebase Auth s'il n'existe pas
      const { createUserWithEmailAndPassword } = await import('firebase/auth');
      
      try {
        // Essayer de créer un nouveau compte
        await createUserWithEmailAndPassword(auth, member.email, tempPassword);
        console.log('Compte Firebase Auth créé pour le membre:', member.email);
      } catch (error: any) {
        if (error.code === 'auth/email-already-in-use') {
          // Le compte existe déjà, on continue sans erreur
          console.log('Compte Firebase Auth existant, connexion...');
        } else {
          throw error;
        }
      }
      
      // Définir manuellement les données du membre dans le contexte
      setMember(member);
      setAdmin(null);
      setUser({ uid: member.id, email: member.email } as User);
      
    } catch (error) {
      console.error('Erreur lors de la connexion par lien magique:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Erreur de déconnexion:', error);
      throw error;
    }
  };

  const isAdmin = admin !== null;
  const isMember = member !== null;

  const value: AuthContextType = {
    user,
    member,
    admin,
    loading,
    login,
    loginWithMagicLink,
    logout,
    isAdmin,
    isMember,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
